import { RouteDeviceTracking } from '@/api/apis/route';
import { EtaType } from '@/types';
import { formatDateTimeStringToTimeZone } from './date.helpers';
import { getTxCenterEtaDateTime } from './case.dates.helpers';

export const getSelectedDeviceFromTracking = (deviceTracking?: RouteDeviceTracking) => {
  const deviceId = deviceTracking?.displaySettings?.deviceToDisplay;
  return deviceId ? deviceTracking?.graphs?.find((g) => g.device.id === deviceId) : undefined;
};

type TxCenterStaParams = {
  txEtaHospitalDate?: string | null;
  txEtaHospitalTime?: string | null;
  txCenterTimezone?: string;
  deviceTracking?: RouteDeviceTracking;
  format?: string;
};

export const getTxCenterSta = ({
  txEtaHospitalDate,
  txEtaHospitalTime,
  txCenterTimezone,
  deviceTracking,
  format,
}: TxCenterStaParams): string | null => {
  const selectedDevice = getSelectedDeviceFromTracking(deviceTracking);

  // STA2: TMDX Drive scheduled arrival (higher priority)
  if (selectedDevice?.scheduledArrivalTime) {
    return formatDateTimeStringToTimeZone(selectedDevice.scheduledArrivalTime, txCenterTimezone, format);
  }

  // STA1: Logistics / case data
  return getTxCenterEtaDateTime(txEtaHospitalDate, txEtaHospitalTime, txCenterTimezone, format);
};

export const getTxCenterRealTimeEta = (
  deviceTracking?: RouteDeviceTracking,
  txCenterTimezone?: string,
  format?: string,
): string | null => {
  const selectedDevice = getSelectedDeviceFromTracking(deviceTracking);

  if (selectedDevice?.estimatedArrivalTime) {
    return formatDateTimeStringToTimeZone(selectedDevice.estimatedArrivalTime, txCenterTimezone, format);
  }

  return null;
};

/**
 * Returns highest priority arrival time: ETA > STA2 > STA1.
 * Used in contexts where only one time value is displayed (e.g., CaseCard, FacilityCard).
 */
export const getTxCenterArrivalTime = (
  params: TxCenterStaParams,
): { type: EtaType.txCenterSta | EtaType.txCenterEta; value: string } | null => {
  const { txCenterTimezone, deviceTracking, format } = params;

  // Priority 1: Real-time ETA
  const eta = getTxCenterRealTimeEta(deviceTracking, txCenterTimezone, format);
  if (eta) return { type: EtaType.txCenterEta, value: eta };

  // Priority 2-3: STA2 > STA1
  const sta = getTxCenterSta(params);
  if (sta) return { type: EtaType.txCenterSta, value: sta };

  return null;
};
